/**
 * YUMIX — Aggressive client-side image compression.
 *
 * STRATEGY (Anti-OOM mobile, Avr. 2026):
 *   • Hard cap: max 800 px wide
 *   • Force JPEG output @ quality 0.6 (production-safe, ~150-300 KB output)
 *   • WebP only as opt-in (FatSecret/admin paths) — JPEG everywhere user-facing
 *   • Quality ladder 0.6 → 0.5 → 0.4 → 0.35 to hit < 400 KB target
 *   • Uses OffscreenCanvas when available for off-main-thread rendering.
 *
 * Why JPEG over WebP for client uploads ? WebP encode on iOS Safari uses
 * software path (Skia) which can spike 80-120 MB heap on a 4032×3024 photo
 * → black-screen OOM. JPEG encode is hardware-accelerated on iOS/Android.
 */

const MAX_WIDTH_DEFAULT = 800;
const TARGET_BYTES = 400 * 1024;
const HARD_CEILING_BYTES = 1.5 * 1024 * 1024; // safety net if quality ladder fails

// WebP detection — only used if caller explicitly opts in via {preferWebP:true}.
let _webpSupported = null;
function supportsWebP() {
  if (_webpSupported !== null) return _webpSupported;
  try {
    const c = document.createElement('canvas');
    c.width = c.height = 2;
    _webpSupported = c.toDataURL('image/webp').startsWith('data:image/webp');
  } catch { _webpSupported = false; }
  return _webpSupported;
}

export function compressImage(file, opts = {}) {
  const { maxWidth = MAX_WIDTH_DEFAULT, quality = 0.6, preferWebP = false } = opts;
  return new Promise((resolve, reject) => {
    const finalize = async (bitmap) => {
      try {
        let { width, height } = bitmap;
        const maxDim = Math.max(width, height);
        if (maxDim > maxWidth) {
          const scale = maxWidth / maxDim;
          width = Math.max(1, Math.round(width * scale));
          height = Math.max(1, Math.round(height * scale));
        }
        const useOffscreen = typeof OffscreenCanvas !== 'undefined';
        const canvas = useOffscreen
          ? new OffscreenCanvas(width, height)
          : Object.assign(document.createElement('canvas'), { width, height });
        if (!useOffscreen) { canvas.width = width; canvas.height = height; }
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'medium';
        ctx.drawImage(bitmap, 0, 0, width, height);
        try { bitmap.close && bitmap.close(); } catch { /* noop */ }

        const mime = preferWebP && supportsWebP() ? 'image/webp' : 'image/jpeg';

        // Iterative quality ladder: try 0.6 first, step down until < TARGET_BYTES
        const ladder = [quality, 0.5, 0.4, 0.35];
        let result = null;
        for (const q of ladder) {
          const base64 = await encodeCanvas(canvas, mime, q);
          const sizeBytes = Math.ceil((base64.length * 3) / 4);
          if (sizeBytes <= TARGET_BYTES || q === ladder[ladder.length - 1]) {
            result = { base64, sizeBytes, mime, quality: q, width, height };
            break;
          }
        }

        // Last-resort: if STILL above hard ceiling, downscale further + retry
        if (result && result.sizeBytes > HARD_CEILING_BYTES && maxWidth > 400) {
          return resolve(
            await compressImage(file, { maxWidth: Math.round(maxWidth * 0.75), quality: 0.4, preferWebP })
          );
        }

        if (!useOffscreen) { canvas.width = 0; canvas.height = 0; }
        resolve(result.base64);
      } catch (err) {
        reject(err);
      }
    };

    if (typeof createImageBitmap === 'function') {
      let settled = false;
      const t = setTimeout(() => { if (!settled) reject(new Error('Image decode timeout')); }, 15000);
      createImageBitmap(file)
        .then((bitmap) => { settled = true; clearTimeout(t); finalize(bitmap); })
        .catch(() => {
          settled = true; clearTimeout(t);
          legacyFallback(file, maxWidth, quality).then(resolve).catch(reject);
        });
    } else {
      legacyFallback(file, maxWidth, quality).then(resolve).catch(reject);
    }
  });
}

// Encode canvas → pure base64 (no data: prefix), WebP preferred, JPEG fallback
function encodeCanvas(canvas, mime, q) {
  return new Promise((resolve, reject) => {
    if (canvas instanceof OffscreenCanvas) {
      canvas.convertToBlob({ type: mime, quality: q })
        .then(blob => {
          const r = new FileReader();
          r.onload = (e) => resolve(e.target.result.split(',')[1]);
          r.onerror = () => reject(new Error('Blob read failed'));
          r.readAsDataURL(blob);
        })
        .catch(reject);
    } else {
      const url = canvas.toDataURL(mime, q);
      resolve(url.split(',')[1]);
    }
  });
}

function legacyFallback(file, maxWidth, quality) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    const t = setTimeout(() => { URL.revokeObjectURL(url); reject(new Error('Image load timeout')); }, 15000);
    img.onload = () => {
      clearTimeout(t);
      URL.revokeObjectURL(url);
      try {
        let { width, height } = img;
        const maxDim = Math.max(width, height);
        if (maxDim > maxWidth) {
          const scale = maxWidth / maxDim;
          width = Math.max(1, Math.round(width * scale));
          height = Math.max(1, Math.round(height * scale));
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'medium';
        ctx.drawImage(img, 0, 0, width, height);
        // JPEG-first strategy (matches main path) — guaranteed hardware-accelerated on all engines
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        canvas.width = 0; canvas.height = 0;
        resolve(dataUrl.split(',')[1]);
      } catch (err) { reject(err); }
    };
    img.onerror = () => { clearTimeout(t); URL.revokeObjectURL(url); reject(new Error('Failed to load image')); };
    img.src = url;
  });
}
